import type { DeepResearchSource } from './types';

const ACADEMIC_HOST_HINTS = ['arxiv.org', 'doi.org', 'pubmed.ncbi.nlm.nih.gov', 'acm.org', 'ieee.org', 'nature.com', 'springer.com'];
const HIGH_TRUST_HOST_HINTS = ['gov', 'edu', 'org'];

const normalizeUrl = (value?: string | null) => {
  if (!value) return null;

  try {
    const url = new URL(value);
    if (!['http:', 'https:'].includes(url.protocol)) {
      return null;
    }

    url.hash = '';
    return url.toString();
  } catch {
    return null;
  }
};

const scoreDomain = (hostname?: string | null) => {
  if (!hostname) return 45;
  const lower = hostname.toLowerCase();

  if (ACADEMIC_HOST_HINTS.some((hint) => lower.includes(hint))) {
    return 90;
  }

  if (HIGH_TRUST_HOST_HINTS.some((hint) => lower.endsWith(`.${hint}`) || lower === hint)) {
    return 80;
  }

  if (lower.includes('github.com')) {
    return 75;
  }

  return 60;
};

export function validateAndNormalizeSources(sources: DeepResearchSource[]) {
  const deduped = new Map<string, DeepResearchSource>();
  const rejected: DeepResearchSource[] = [];

  sources.forEach((source, index) => {
    const normalizedUrl = normalizeUrl(source.url);
    const domain = normalizedUrl ? new URL(normalizedUrl).hostname : source.domain || null;
    const title = source.title?.trim();

    if (!title) {
      rejected.push({ ...source, valid: false, qualityScore: 0, provenance: 'rejected: missing title' });
      return;
    }

    if (source.url && !normalizedUrl) {
      rejected.push({ ...source, valid: false, qualityScore: 0, provenance: 'rejected: malformed url' });
      return;
    }

    const qualityScore = Math.min(98, Math.max(source.qualityScore || 0, scoreDomain(domain)));
    const normalized: DeepResearchSource = {
      ...source,
      id: source.id || `S${index + 1}`,
      title,
      url: normalizedUrl,
      domain,
      valid: true,
      qualityScore,
      provenance: source.provenance || `${source.provider}:${source.type}`,
    };

    const key = normalized.url || `${normalized.type}:${normalized.title.toLowerCase()}`;
    const existing = deduped.get(key);
    if (!existing || normalized.qualityScore > existing.qualityScore) {
      deduped.set(key, normalized);
    }
  });

  const validSources = Array.from(deduped.values()).map((source, index) => ({
    ...source,
    id: `S${index + 1}`,
  }));

  return {
    validSources,
    rejected,
  };
}
